import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    title: String,
    text: String,
    url: String,
    copiedMessage: { type: String, default: "Lien copié" },
    fallbackMessage: { type: String, default: "Copiez ce lien :" }
  }

  disconnect() {
    this._removeToast()
  }

  async share() {
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({
          title: this.titleValue,
          text: this.textValue,
          url: this.urlValue
        })
        return
      } catch (error) {
        if (error.name === "AbortError") return
      }
    }
    await this._copyToClipboard()
  }

  async _copyToClipboard() {
    if (await this._tryClipboardApi()) {
      this._showToast(this.copiedMessageValue)
      return
    }
    if (this._tryExecCommand()) {
      this._showToast(this.copiedMessageValue)
      return
    }
    this._showFallbackToast()
  }

  async _tryClipboardApi() {
    try {
      if (!navigator.clipboard?.writeText) return false
      await navigator.clipboard.writeText(this.urlValue)
      return true
    } catch {
      return false
    }
  }

  _tryExecCommand() {
    try {
      const ta = document.createElement("textarea")
      ta.value = this.urlValue
      ta.setAttribute("readonly", "")
      ta.style.position = "fixed"
      ta.style.left = "-9999px"
      document.body.appendChild(ta)
      ta.select()
      const ok = document.execCommand("copy")
      document.body.removeChild(ta)
      return ok
    } catch {
      return false
    }
  }

  _showToast(message) {
    this._removeToast()
    const toast = this._buildToast(message)
    document.body.appendChild(toast)
    this._toast = toast
    this._toastTimeout = setTimeout(() => this._removeToast(), 3000)
  }

  _showFallbackToast() {
    this._removeToast()
    const toast = document.createElement("div")
    toast.className = "fixed top-4 right-4 z-50 max-w-sm toast-enter"
    toast.setAttribute("role", "status")
    toast.setAttribute("data-share-toast", "")

    const inner = document.createElement("div")
    inner.className = "toast-glass toast-notice flex flex-col gap-2 px-4 py-3"

    const label = document.createElement("p")
    label.className = "text-sm font-medium text-primary"
    label.textContent = this.fallbackMessageValue

    const input = document.createElement("input")
    input.type = "text"
    input.readOnly = true
    input.value = this.urlValue
    input.className = "text-xs px-2 py-1 rounded bg-transparent border border-muted text-primary w-full"
    input.addEventListener("focus", () => input.select())

    inner.appendChild(label)
    inner.appendChild(input)
    toast.appendChild(inner)
    document.body.appendChild(toast)

    this._toast = toast
    this._toastTimeout = setTimeout(() => this._removeToast(), 8000)
  }

  _buildToast(message) {
    const toast = document.createElement("div")
    toast.className = "fixed top-4 right-4 z-50 max-w-sm toast-enter"
    toast.setAttribute("role", "status")
    toast.setAttribute("data-share-toast", "")

    const inner = document.createElement("div")
    inner.className = "toast-glass toast-notice flex items-center gap-3 px-4 py-3"

    const text = document.createElement("p")
    text.className = "text-sm font-medium text-primary"
    text.textContent = message

    inner.appendChild(text)
    toast.appendChild(inner)
    return toast
  }

  _removeToast() {
    if (this._toastTimeout) clearTimeout(this._toastTimeout)
    if (this._toast && this._toast.parentNode) {
      this._toast.remove()
    }
    this._toast = null
  }
}
